// Mapping from existing cash-flow cost groups / positions
// (as returned by useCashFlowStatementDetails) to the
// user's "Monthly CF-ETB" line item layout.
//
// Matching is done by case-insensitive label match against the
// group row label (e.g. "Admin Cost") and, when `positions` is set,
// against the position row label inside that group (e.g. "Salary").
//
// `excludePositions` means: sum the whole group MINUS the listed positions.

export type MatchRule =
  | { group: string } // whole group
  | { group: string; positions: string[] } // only these positions
  | { group: string; excludePositions: string[] }; // group minus these

export type SectionLine = {
  label: string;
  matches: MatchRule[];
};

export type ReportSection = {
  title: string;
  subtotalLabel: string;
  lines: SectionLine[];
};

export const COGS_SECTION: ReportSection = {
  title: "Operating Outflows — Cost of Goods Sold",
  subtotalLabel: "Total Cost of Goods Sold",
  lines: [
    {
      label: "Purchased Goods",
      matches: [
        { group: "Purchased Goods" },
        { group: "Sourcing Fresh" },
        {
          group: "Sourcing FMCG",
          positions: ["Direct support", "Purchase", "Purchased"],
        },
        // Fallback: whole Sourcing FMCG group in case positions are named differently
        { group: "Sourcing FMCG" },
        { group: "Warehouse", positions: ["loading unloading"] },
      ],

    },
    {
      label: "Packaging",
      matches: [
        { group: "Packaging" },
        { group: "Warehouse", positions: ["PP Bag"] },
      ],
    },
    {
      label: "Lost and Spoiled Goods",
      matches: [{ group: "Lost and Spoiled Goods" }],
    },
    {
      label: "Customer Refunds",
      matches: [
        {
          group: "Customer Service",
          positions: [
            "Refund",
            "Tele Subscription",
            "SGL commission withdrawal",
          ],
        },
      ],
    },
    {
      label: "Payment Provider Fees",
      matches: [
        { group: "Payment Provider Fees" },
        { group: "Finance", positions: ["Payment Transfer Fees"] },
        { group: "Admin Cost", positions: ["Payment Transfer Fees", "Transfer Fees"] },
      ],
    },
    {
      label: "Delivery Service",
      matches: [
        { group: "Delivery Service" },
        { group: "Admin Cost", positions: ["Fuel"] },
      ],
    },
  ],
};

export const SGA_SECTION: ReportSection = {
  title: "Admin, Selling and General",
  subtotalLabel: "Total Admin, Selling and General",
  lines: [
    {
      label: "Salary, and Allowance",
      matches: [
        {
          group: "Admin Cost",
          positions: [
            "Salary",
            "HR Job Post",
            "Allowance",
            "Phone Package Allowance",
            "Travel & Transport",
            "Travel & Transport (Grant)",
            "Travel & Transport (Zeami)",
          ],
        },
        { group: "Finance", positions: ["Transportation"] },
        { group: "HR", positions: ["Overtime"] },
        { group: "Warehouse", positions: ["Warehouse Salary", "Warehouse Overtime"] },
      ],
    },

    {
      label: "Tax",
      matches: [
        {
          group: "Finance",
          positions: [
            "Auditor Payment",
            "Income Tax",
            "Pension",
            "VAT",
            "Service charge",
            "Withholding Taxes",
            "Penalty",
            "Tax",
          ],
        },
        { group: "Admin Cost", positions: ["Authentication fee", "Autentication fee"] },
      ],
    },
    {
      label: "Office Rent",
      matches: [{ group: "Admin Cost", positions: ["Rent"] }],
    },
    {
      label: "Warehouse Rent",
      matches: [{ group: "Warehouse", positions: ["Warehouse Rent"] }],
    },
    {
      label: "Utilities Office",
      matches: [
        {
          group: "Admin Cost",
          positions: [
            "Asset",
            "Cleaning",
            "Office Utility Payment",
            "Utility Payment",
            "Office WIFI payment",
            "Pad Print",
            "POS Machine",
          ],
        },
        {
          group: "HR",
          positions: [
            "Allowance",
            "Lunch Payment",
            "Office Supplies Purchase",
            "Stationary",
            "Water",
          ],
        },
        { group: "Finance", positions: ["Pad Print", "POS Machine"] },
        { group: "Warehouse", positions: ["Wifi"] },
      ],
    },
    {
      label: "Utilities Warehouse",
      matches: [
        {
          group: "Warehouse",
          excludePositions: [
            "Warehouse Rent",
            "Warehouse Salary",
            "PP Bag",
            "Warehouse Overtime",
            "Wifi",
            "Warehouse Petty Cash",
            "loading unloading",
            "Breakfast",
          ],
        },
      ],
    },

    {
      label: "Consulting & Professional Services",
      matches: [{ group: "Legal and Professional Fees" }],
    },
    {
      label: "Marketing Expense",
      matches: [
        { group: "Marketing" },
        { group: "Finance", positions: ["Survey Payment"] },
      ],
    },
    {
      label: "Technology Costs",
      matches: [{ group: "Technology" }],
    },
    {
      label: "Assets",
      matches: [
        { group: "Admin Cost", positions: ["Asset"] },
        { group: "HR", positions: ["Computer"] },
      ],
    },
    {
      label: "Other Costs",
      matches: [
        { group: "Miscellaneous Admin Costs" },
        { group: "Admin Cost", positions: ["Investor Day", "Travel & Transport (Export)"] },
        { group: "Finance", positions: ["Cash Reimburse"] },
        { group: "Warehouse", positions: ["Warehouse Petty Cash", "Breakfast"] },
      ],

    },
  ],
};

export const REPORT_SECTIONS: ReportSection[] = [COGS_SECTION, SGA_SECTION];
